import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import CircularProgress from "@mui/material/CircularProgress";
import "../../competitiveAnalysisModal/index.css";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import { useNitroStore } from "../../../store/Store";
import IRouteValidationResult from "../../competitiveAnalysisModal/components/RouteValidation/types/IRouteValidation";
import RouteValidationService from "../../competitiveAnalysisModal/components/RouteValidation/lib/service";
import RequestPanel from "../../competitiveAnalysisModal/components/RouteValidation/components/requestPanel";
import NitroRequestService from "../../../utils/nitroRequest.service";
import NotificationPanel from "../../competitiveAnalysisModal/components/RouteValidation/components/notificationPanel";
import StatusPanel from "../../competitiveAnalysisModal/components/RouteValidation/components/StatusPanel";
import FindOccupant from "../../competitiveAnalysisModal/components/RouteValidation/components/findOccupant";
import NitroService from "../../../utils/nitro.service";
import EnableOrigin from "./EnableOrigin";

// TODO: leaving app up and coming back next day all params for validate api were blank
// TODO: enable origin loads before validate api so all cards return then re-load after validate completes
// TODO: insights panel closes expanded destination when completing a task for the enable origin flow
const TroubleshootQuote = () => {
  const [
    troubleshootQuoteOpen,
    setTroubleshootQuoteOpen,
    originToTroubleshoot,
    setOriginToTroubleshoot,
    destination,
    triggerRefetchMultiLocation,
    insightsPanelSaved,
    setInsightsPanelSaved,
  ] = useNitroCAStore((state) => [
    state.troubleshootQuoteOpen,
    state.setTroubleshootQuoteOpen,
    state.originToTroubleshoot,
    state.setOriginToTroubleshoot,
    state.destinationToTroubleshoot,
    state.triggerRefetchMultiLocation,
    state.insightsPanelSaved,
    state.setInsightsPanelSaved,
  ]);
  const [
    product,
    mode,
    currentFilters,
    showInsightsPanel,
  ] = useNitroStore((state) => [
    state.product,
    state.mode,
    state.currentFilters,
    state.showInsightsPanel,
  ]);
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [occupant, setOccupant] = useState({ id: "", name: "" });
  const [validation, setValidation] = useState<IRouteValidationResult | null>(
    null,
  );
  const [initialValidation, setInitialValidation] =
    useState<IRouteValidationResult | null>(null);
  const initialValidationRef = useRef<IRouteValidationResult | null>(null);
  const loadTrackerRef = useRef<string | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  type ViewState =
    | "LOOKUP_FORM"
    | "ORIGIN_ALREADY_ADDED"
    | "TASK_LIST"
    | "SUCCESS_RATE_MISSING"
    | "SEARCH_FOR_RAIL_RATE"
    | "ADD_MISSING_ORIGIN_COMPLETE"
    | "MILES_NEEDED_COMPLETE"
    | "TRUCK_RATE_MISSING_COMPLETE"
    | "ADD_FAILURE"
    | "UNKNOWN_LOADING";
  const [currentView, setCurrentView] = useState<ViewState>("UNKNOWN_LOADING");
  const [pendingCompletion, setPendingCompletion] =
    useState<IRouteValidationResult | null>(null);

  // close function
  const reset = () => {
    loadTrackerRef.current = null;
    initialValidationRef.current = null;
    setInitialValidation(null);
    setValidation(null);
    setOccupant({ id: "", name: "" });
    setOriginToTroubleshoot({
      id: "",
      name: "",
      coordinate: [0, 0],
      isCity: false,
    });
    setCurrentView("UNKNOWN_LOADING");
  };
  const handleFlowClose = () => {
    reset();
    setTroubleshootQuoteOpen(false);
  };

  // if there is only one occupant at that origin, we can auto-select it
  useEffect(() => {
    const checkOccupants = async () => {
      const records = await NitroService.getOccupantsByLocationId({
        locationId: originToTroubleshoot.origin.id,
      });
      if (records.length === 1) {
        setOccupant(records[0]);
        setCurrentView("TASK_LIST");
      } else if (records.length >= 1) {
        setCurrentView("LOOKUP_FORM");
      }
    };
    checkOccupants();
  }, [originToTroubleshoot]);

  const handleOccupantSelect = (selectedOccupant: any) => {
    setOccupant(selectedOccupant);
  };

  useEffect(() => {
    if (!occupant.id) return;
    setCurrentView("TASK_LIST");
  }, [occupant]);

  const sendApiErrorEmail = useCallback(
    async (apiName: string, errMsg: string) => {
      try {
        await NitroRequestService.postApiErrorRequest({
          marketer: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
          product,
          mode,
          origin: originToTroubleshoot.origin.name,
          originZipCode: validation?.originZipCode ?? "",
          destination: destination.name,
          destinationZipCode: validation?.destinationZipCode ?? "",
          comments: "",
          api: apiName,
          message: errMsg,
        });
      } catch (error) {
        console.error("postApiErrorRequest - Failed to send email:", error);
      }
    },
    [
      auth.user,
      product,
      mode,
      originToTroubleshoot.origin.name,
      validation,
      destination,
    ],
  );

  const sendRateMissingEmail = useCallback(async () => {
    try {
      await NitroRequestService.postRailRateMissingRequest({
        marketer: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
        product,
        mode,
        origin: originToTroubleshoot.origin.name,
        originZipCode: validation?.originZipCode ?? "",
        destination: destination.name,
        destinationZipCode: validation?.destinationZipCode ?? "",
        comments: "",
      });
    } catch (error) {
      console.error(
        "Requests/missingRateRequest - Failed to send email:",
        error,
      );
      setCurrentView("ADD_FAILURE");
      sendApiErrorEmail(
        "requests/missingRateRequest",
        JSON.stringify(error, Object.getOwnPropertyNames(error), 2),
      );
    }
  }, [
    auth.user?.profile.given_name,
    auth.user?.profile.family_name,
    product,
    mode,
    originToTroubleshoot.origin.name,
    validation?.originZipCode,
    validation?.destinationZipCode,
    destination.name,
    sendApiErrorEmail,
  ]);

  const sendEmail = useCallback(async () => {
    try {
      await NitroRequestService.postMileageRequest({
        marketer: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
        product,
        mode,
        origin: originToTroubleshoot.origin.name,
        originZipCode: validation?.originZipCode ?? "",
        destination: destination.name,
        destinationZipCode: validation?.destinationZipCode ?? "",
        comments: "",
      });
    } catch (error) {
      console.error("MileageRequest - Failed to send email:", error);
    }
  }, [
    auth.user?.profile.given_name,
    auth.user?.profile.family_name,
    product,
    mode,
    originToTroubleshoot.origin.name,
    validation?.originZipCode,
    validation?.destinationZipCode,
    destination.name,
  ]);

  const sendRateReviewEmail = useCallback(async () => {
    try {
      await NitroRequestService.postReviewRequest({
        marketer: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
        origin: originToTroubleshoot.origin.name,
        originZipCode: validation?.originZipCode ?? "",
        destination: destination.name,
        destinationZipCode: validation?.destinationZipCode ?? "",
        customerName: "",
        mode,
        railCarriers: "",
        product,
        month: currentFilters.month,
        comments: "",
      });
    } catch (error) {
      console.error("ReviewRequest - Failed to send email:", error);
    }
  }, [
    auth.user?.profile.given_name,
    auth.user?.profile.family_name,
    originToTroubleshoot.origin.name,
    validation?.originZipCode,
    validation?.destinationZipCode,
    destination.name,
    mode,
    product,
    currentFilters.month,
  ]);

  const sendSQSMessage = useCallback(async () => {
    try {
      if (!destination) {
        throw new Error("No selected location available for cache refresh.");
      }
      await NitroRequestService.postCacheRefresh("destination", {
        destinationId: destination.id,
      });
    } catch (error) {
      setCurrentView("ADD_FAILURE");
      sendApiErrorEmail(
        "cache-refresh",
        JSON.stringify(error, Object.getOwnPropertyNames(error), 2),
      );
      console.error("postCacheRefresh - Failed to send SQS message:", error);
    }
  }, [destination, sendApiErrorEmail]);

  const pollRouteValidation = useCallback(() => {
    const POLL_INTERVAL_MS = 15 * 1000; // 15 seconds
    const TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes
    const startTime = Date.now();
    const intervalId = setInterval(async () => {
      try {
        const result = await RouteValidationService.validate({
          mode,
          product,
          originLocationId: originToTroubleshoot.origin.id,
          originOccupantId: occupant.id || "",
          destinationId: destination.id,
          shipDate: currentFilters.month,
          isCity: destination.isCity,
        });
        if (result.hasRailCache) {
          setCurrentView("ADD_MISSING_ORIGIN_COMPLETE");
          setShowNotification(true);
          clearInterval(intervalId);
          sendRateReviewEmail();
          return;
        }

        if (Date.now() - startTime >= TIMEOUT_MS) {
          console.log(
            "Timeout: Rail cache not available after 3 minutes. Sending rate review email.",
          );
          sendRateReviewEmail();
          clearInterval(intervalId);
        }
      } catch (error) {
        sendRateReviewEmail();
        console.error("Polling API call failed:", error);
        clearInterval(intervalId); // Stop polling on error
      }
    }, POLL_INTERVAL_MS);
  }, [
    mode,
    product,
    originToTroubleshoot.origin.id,
    occupant.id,
    currentFilters.month,
    sendRateReviewEmail,
    destination,
  ]);

  const handleComplete = useCallback(
    (result: any) => {
      if (
        mode === "TRUCK" &&
        result.hasTruckRoute &&
        result.hasValidTruckRateForShipWindow &&
        !result.hasTruckMileage
      ) {
        setCurrentView("MILES_NEEDED_COMPLETE");
        sendEmail();
      } else if (
        mode === "TRUCK" &&
        (!result.hasTruckRoute || !result.hasValidTruckRateForShipWindow)
      ) {
        setCurrentView("TRUCK_RATE_MISSING_COMPLETE");
        sendRateMissingEmail();
      } else if (
        mode === "RAIL" &&
        result.hasRailRouteFromOrigin &&
        result.hasRailRouteFromServingOriginCarrier &&
        result.hasRailRouteToDestination &&
        result.hasRailRouteToServingDestinationCarrier &&
        !result.hasRailCache
      ) {
        setCurrentView("SEARCH_FOR_RAIL_RATE");
        sendSQSMessage();
        pollRouteValidation();
      } else if (
        mode === "RAIL" &&
        (!result.hasRailRouteFromOrigin ||
          !result.hasRailRouteFromServingOriginCarrier ||
          !result.hasRailRouteToDestination ||
          !result.hasRailRouteToServingDestinationCarrier ||
          !result.railRouteCanBeBuilt)
      ) {
        setCurrentView("SUCCESS_RATE_MISSING");
        sendRateMissingEmail();
      } else if (
        mode === "TRUCK" &&
        result.hasOutboundMode &&
        result.hasKochOrCompetitorOccupant &&
        result.originCanStoreProduct &&
        result.hasTruckRoute &&
        result.hasValidTruckRateForShipWindow &&
        result.hasInboundMode &&
        result.inStateTheyCompeteIn &&
        result.hasTruckMileage
      ) {
        setCurrentView("ADD_MISSING_ORIGIN_COMPLETE");
      } else {
        setCurrentView("ADD_FAILURE");
        sendApiErrorEmail(
          "RouteValidation",
          `Uncovered case - No origin added: ${JSON.stringify(
            result,
            null,
            2,
          )}`,
        );
      }
    },
    [
      mode,
      sendEmail,
      sendSQSMessage,
      pollRouteValidation,
      sendRateMissingEmail,
      sendApiErrorEmail,
    ],
  );

  useEffect(() => {
    if (pendingCompletion) {
      handleCompleteRef.current(pendingCompletion);
      setPendingCompletion(null);
    }
  }, [pendingCompletion]);

  const handleCompleteRef = useRef(handleComplete);
  useEffect(() => {
    handleCompleteRef.current = handleComplete;
  }, [handleComplete]);

  const sendApiErrorEmailRef = useRef(sendApiErrorEmail);

  // Keep the ref updated with the latest version of the function on every render
  useEffect(() => {
    sendApiErrorEmailRef.current = sendApiErrorEmail;
  }, [sendApiErrorEmail]);

  const load = useCallback(async () => {
    if (!originToTroubleshoot.origin.id || !occupant.id) return;

    setValidation(null);
    setIsLoading(true);

    try {
      const result = await RouteValidationService.validate({
        mode,
        product,
        originLocationId: originToTroubleshoot.origin.id,
        originOccupantId: occupant.id,
        destinationId: destination.id,
        shipDate: currentFilters.month,
        isCity: destination.isCity,
      });
      setValidation(result as any);
      if (!initialValidation && result) {
        setInitialValidation(JSON.parse(JSON.stringify(result)));
      }
    } catch (err) {
      setCurrentView("ADD_FAILURE");
      sendApiErrorEmailRef.current(
        "/RouteValidation?",
        JSON.stringify(err, Object.getOwnPropertyNames(err), 2),
      );
      console.error(err);
    }
    setIsLoading(false);
  }, [
    mode,
    product,
    originToTroubleshoot,
    occupant,
    destination,
    currentFilters,
    initialValidation,
  ]);

  useEffect(() => {
    if (!validation) {
      return;
    }

    initialValidationRef.current ??= JSON.parse(JSON.stringify(validation));

    const checks = [
      !initialValidation?.inStateTheyCompeteIn,
      !initialValidation?.originCanStoreProduct,
      !initialValidation?.hasOutboundMode,
      mode.toLowerCase() === "rail" &&
        initialValidation?.hasRailRouteFromOrigin &&
        !initialValidation?.hasRailRouteFromServingOriginCarrier,
      !initialValidation?.destinationCanStoreProduct,
      !initialValidation?.hasInboundMode,
      mode.toLowerCase() === "rail" &&
        initialValidation?.hasRailRouteToDestination &&
        !initialValidation?.hasRailRouteToServingDestinationCarrier,
    ];
    const totalNum = checks.filter(Boolean).length;

    if (totalNum === 0) {
      handleCompleteRef.current(validation);
    }
  }, [validation, initialValidation, mode]);

  useEffect(() => {
    if (
      !destination ||
      !product ||
      !mode ||
      !originToTroubleshoot.origin.id ||
      !occupant.id
    ) {
      setValidation(null);
      return;
    }

    const dependencyKey = `${originToTroubleshoot.origin.id}-${occupant.id}-${mode}-${product}-${destination.id}`;

    if (dependencyKey === loadTrackerRef.current) {
      return;
    }

    loadTrackerRef.current = dependencyKey;
    load();
  }, [destination, product, mode, originToTroubleshoot, occupant, load]);

  useEffect(() => {
    if (!showInsightsPanel && troubleshootQuoteOpen) {
      load();
      if (insightsPanelSaved) {
        triggerRefetchMultiLocation();
        setInsightsPanelSaved(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInsightsPanel]);

  if (!troubleshootQuoteOpen) {
    return null;
  }

  const renderCurrentPanel = () => {
    switch (currentView) {
      case "TASK_LIST":
        return (
          <EnableOrigin
            selectedOccupant={occupant}
            validation={initialValidation}
            onCompleteChange={handleComplete}
          />
        );
      case "SUCCESS_RATE_MISSING":
        return (
          <StatusPanel
            originName={originToTroubleshoot.origin.name}
            onClose={handleFlowClose}
            title="Rail Rate Missing"
            message={
              <>
                <p>
                  Looks like you did the hard work to add the origin, but the
                  rail rate is still missing.
                </p>
                <p>
                  No worries-we&apos;ve sent an email to the team to get
                  everything sorted. We&apos;ll let you know as soon as the
                  origin is added!
                </p>
              </>
            }
            buttonText="Close"
          />
        );
      case "SEARCH_FOR_RAIL_RATE":
        return (
          <StatusPanel
            originName={originToTroubleshoot.origin.name}
            onClose={handleFlowClose}
            title="Searching For Rail Rates"
            message={
              <p>
                Attempting to add this origin. Please note the rate won&apos;t
                be available for use until it&apos;s verified.
              </p>
            }
            buttonText="Close"
          />
        );
      case "ADD_MISSING_ORIGIN_COMPLETE":
        return (
          <StatusPanel
            originName={originToTroubleshoot.origin.name}
            onClose={handleFlowClose}
            title="Tasks Complete - Origin Added"
            message={
              <p>
                The origin is now available, you&apos;re all set to return to
                quoting.
              </p>
            }
            buttonText="Return to Quoting"
          />
        );
      case "MILES_NEEDED_COMPLETE":
        return (
          <StatusPanel
            originName={originToTroubleshoot.origin.name}
            onClose={handleFlowClose}
            title="Miles Needed"
            message={
              <>
                <p>
                  Looks like you did the hard work to add the origin, but we
                  still need to grab the miles to get everything in place.
                </p>
                <p>
                  They&apos;re required for the origin to show up, so we&apos;ve
                  sent an email to request them. We&apos;ll let you know as soon
                  as the origin is added!
                </p>
              </>
            }
            buttonText="Return to Quoting"
          />
        );
      case "TRUCK_RATE_MISSING_COMPLETE":
        return (
          <StatusPanel
            originName={originToTroubleshoot.origin.name}
            onClose={handleFlowClose}
            title="Truck Rate Missing"
            message={
              <>
                <p>
                  Looks like you did the hard work to add the origin, but it
                  looks like logistics isn&apos;t calling guidance for this OD
                  pair yet.
                </p>
                <p>
                  No worries-we&apos;ve sent an email to the team to get
                  everything sorted. We&apos;ll let you know as soon as the
                  origin is added!
                </p>
              </>
            }
            buttonText="Return to Quoting"
          />
        );
      case "ADD_FAILURE":
        return (
          <StatusPanel
            originName={originToTroubleshoot.origin.name}
            onClose={handleFlowClose}
            title="Oops! Something Went Wrong"
            message={
              <p>
                We&apos;ve notified our team and will let you know once
                it&apos;s fixed—thank you for your patience
              </p>
            }
            buttonText="Close"
          />
        );
      case "LOOKUP_FORM":
        return <FindOccupant setOccupant={handleOccupantSelect} />;
      default:
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </div>
        );
    }
  };

  const getTitle = () => {
    switch (currentView) {
      case "LOOKUP_FORM":
        return "Enable Origin";
      case "ORIGIN_ALREADY_ADDED":
      case "TASK_LIST":
        return "Enable Origin";
      case "SUCCESS_RATE_MISSING":
      case "SEARCH_FOR_RAIL_RATE":
        return "Add Missing Origin";
      case "ADD_MISSING_ORIGIN_COMPLETE":
      case "MILES_NEEDED_COMPLETE":
      case "TRUCK_RATE_MISSING_COMPLETE":
        return "Troubleshoot Missing Origin";
      default:
        return "Enable Origin";
    }
  };

  return (
    <>
      {showNotification && (
        <NotificationPanel
          title="Tasks Complete - Origin Added"
          message="The origin is now available, you're all set to return to quoting."
          onClose={() => setShowNotification(false)}
        />
      )}
      <RequestPanel
        isSaving={false}
        isLoading={isLoading}
        goBack={handleFlowClose}
        title={getTitle()}
      >
        {renderCurrentPanel()}
      </RequestPanel>
    </>
  );
};

export default TroubleshootQuote;
